import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faCheck,
	faTimesCircle,
} from "@fortawesome/free-solid-svg-icons"
import {
	updateEmploye,
	postEmployesReset,
	deleteEmployeRH,
	deleteEmployeReset,
} from "../../../store/actions/depannageActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import { useRights } from "../../../hooks/rights"

const FIELDS = [
	{ key: "prenom", label: "Prénom", required: true },
	{ key: "nom", label: "Nom", required: true },
	{ key: "email", label: "E-mail" },
	{ key: "tel_mobile", label: "Tél. mobile" },
	{ key: "tel_urgence", label: "Tél. urgence" },
]

const EmployeModalV2 = ({ employe, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.masterReducer.postEmploye)
	const deleteState = useSelector((s) => s.masterReducer.deleteEmploye)
	const availableGroups = useSelector((s) => s.masterReducer.groups)
	// L'attribution aux groupes reste réservée au boss : c'est le point
	// d'escalade de privilèges (un admin pourrait sinon se donner `boss`).
	// L'admin VOIT les groupes de l'employé, il ne peut pas les changer. Miroir
	// serveur : EmployeRHView.post ignore la clé `groups` hors droit.
	const { hasRight } = useRights()
	const peutModifierGroupes = hasRight("config_rh_groupes")
	const isCreate = !employe.id
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [form, setForm] = useState({
		prenom: employe.prenom || "",
		nom: employe.nom_famille || "",
		email: employe.email || "",
		tel_mobile: employe.tel_mobile || "",
		tel_urgence: employe.tel_urgence || "",
		actif: employe.actif !== false,
		groups: employe.groups || [],
	})
	const update = (k, v) => setForm((p) => ({ ...p, [k]: v }))
	const toggleGroup = (g) =>
		setForm((p) => ({
			...p,
			groups: p.groups.includes(g)
				? p.groups.filter((x) => x !== g)
				: [...p.groups, g],
		}))

	// Fermeture après une sauvegarde OU une suppression réussie.
	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postEmployesReset())
			dispatch(deleteEmployeReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postEmployesReset())
			dispatch(deleteEmployeReset())
		},
		[],
	)

	// Échec de suppression : on ressort du mode « Confirmer ? ».
	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	const invalid = !form.prenom.trim() || !form.nom.trim()
	const save = () => dispatch(updateEmploye({ id: employe.id, ...form }))
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteEmployeRH(employe.id))
	}
	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={isCreate ? "Nouvel employé" : "Éditer l'employé"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				{FIELDS.map((f) => (
					<div key={f.key} style={gs.fieldRow}>
						<label style={gs.label}>
							{f.label}
							{f.required && (
								<span style={{ color: COLORS.danger }}> *</span>
							)}
						</label>
						<input
							style={gs.input}
							value={form[f.key]}
							onChange={(e) => update(f.key, e.target.value)}
						/>
					</div>
				))}
				<div style={gs.fieldRow}>
					<label style={gs.label}>Actif</label>
					<label
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: 8,
							cursor: "pointer",
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						<input
							type="checkbox"
							checked={form.actif}
							onChange={(e) => update("actif", e.target.checked)}
						/>
						{form.actif ? "Compte actif" : "Compte désactivé"}
					</label>
				</div>
				<div style={{ ...gs.fieldRow, alignItems: "flex-start" }}>
					<label style={gs.label}>Groupes</label>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 12,
							flex: 1,
						}}
					>
						{availableGroups.length === 0 && (
							<span
								style={{
									fontSize: 13,
									color: COLORS.textLight,
								}}
							>
								Aucun groupe.
							</span>
						)}
						{availableGroups.map((g) => (
							<label
								key={g}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 6,
									fontSize: 13,
									color: peutModifierGroupes
										? COLORS.text
										: COLORS.textMuted,
									cursor: peutModifierGroupes
										? "pointer"
										: "not-allowed",
								}}
							>
								<input
									type="checkbox"
									disabled={!peutModifierGroupes}
									checked={form.groups.includes(g)}
									onChange={() => toggleGroup(g)}
								/>
								{g}
							</label>
						))}
						{!peutModifierGroupes && (
							<span
								style={{
									flexBasis: "100%",
									fontSize: 12,
									color: COLORS.textLight,
								}}
							>
								L'attribution aux groupes est réservée au boss.
							</span>
						)}
					</div>
				</div>
				{isCreate && (
					<p
						style={{
							margin: "4px 0 0",
							fontSize: 12,
							color: COLORS.textLight,
						}}
					>
						Un compte sera créé avec un mot de passe par défaut ;
						l'employé devra le changer à la première connexion.
					</p>
				)}
				{(postState.error || deleteState.error) && (
					<div
						style={{
							marginTop: 10,
							fontSize: 13,
							color: COLORS.danger,
							textAlign: "center",
						}}
					>
						<FontAwesomeIcon icon={faTimesCircle} />{" "}
						{(postState.error && postState.error.message) ||
							(deleteState.error && deleteState.error.message) ||
							"Une erreur est survenue."}
					</div>
				)}
			</div>
		</CustomModal>
	)
}

export default EmployeModalV2
